import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { API_ENDPOINTS } from '../config';
import {
  IconLock,
  IconRefresh,
  IconStar,
  IconSearch,
  IconX,
  IconList,
  IconCheck,
} from '../components/Icons';
import PageHeading from '../components/PageHeading';
import './ResultsSpreadsheetPage.css';
import { getBearerAuthHeaders } from '../utils/authFetchHeaders';
import { getEntityDisplayName } from '../utils/activeEntity';

const NEGATIVE_NUMBER_RE = /^\(?-?\$?\(?-?\d/;
/** Words a "section header" row is mostly composed of. Used to detect header
 *  rows that appear in the middle of the data (e.g. "Day Ranges | Monday | …"
 *  and "Night Ranges | Monday | …" in the same Past Results sheet). */
const HEADER_HINT_WORDS = new Set([
  'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday',
  'total', 'totals', 'notes', 'date', 'sum', 'summary',
]);

/** Detect cells like "-$3,588", "-1,234", "(123)" — used for red coloring. */
function isNegativeCell(value) {
  if (value == null) return false;
  const text = String(value).trim();
  if (!text) return false;
  if (text.startsWith('(') && text.endsWith(')')) return true;
  if (text.startsWith('-')) return NEGATIVE_NUMBER_RE.test(text);
  return false;
}

// Delegates to the shared helper so X-RiskLo-Entity rides along — building
// the Authorization header locally silently dropped it, which made the page
// query Vector no matter which brand was selected.
function buildAuthHeaders() {
  return getBearerAuthHeaders();
}

function normalizeCell(value) {
  if (value == null) return '';
  return String(value);
}

function classifyRow(row) {
  if (!Array.isArray(row)) return { kind: 'blank', cells: [] };
  const cells = row.map((c) => normalizeCell(c));
  const trimmed = cells.map((c) => c.trim());
  const filledCount = trimmed.filter((c) => c !== '').length;
  if (filledCount === 0) return { kind: 'blank', cells };

  const firstCell = trimmed[0] || '';
  const restNonEmpty = trimmed.slice(1).filter((c) => c !== '');

  // Section header: first cell has text, the rest is mostly weekday/total/notes labels.
  if (firstCell && restNonEmpty.length >= 3) {
    const headerHits = restNonEmpty.filter((c) => HEADER_HINT_WORDS.has(c.toLowerCase())).length;
    if (headerHits / restNonEmpty.length >= 0.6) {
      return { kind: 'header', cells };
    }
  }

  // Subtotal: empty first cell with values in the rest of the row.
  if (!firstCell && restNonEmpty.length >= 2) {
    return { kind: 'subtotal', cells };
  }

  return { kind: 'data', cells };
}

/** If row 0 is a single-cell title (like a date) and row 1 looks like a real
 *  header row, render row 0 as a caption banner above the table instead of
 *  shoving it into <thead> with empty trailing columns. */
function detectCaption(rows) {
  if (!Array.isArray(rows) || rows.length < 2) return null;
  const r0Cells = (rows[0] || []).map(normalizeCell).map((c) => c.trim());
  const filled = r0Cells.filter((c) => c !== '').length;
  if (filled !== 1 || !r0Cells[0]) return null;
  const r1NonEmpty = (rows[1] || [])
    .map(normalizeCell)
    .map((c) => c.trim())
    .filter((c) => c !== '');
  if (r1NonEmpty.length < 3) return null;
  return r0Cells[0];
}

function rowMatchesQuery(cells, lowerQuery) {
  if (!lowerQuery) return true;
  return cells.some((c) => c.toLowerCase().includes(lowerQuery));
}

/** Render text with a <mark> around every case-insensitive match of `query`. */
function highlightMatch(text, query) {
  if (!query) return text;
  const haystack = String(text);
  const needle = query;
  if (!haystack || !needle) return text;
  const lowerHaystack = haystack.toLowerCase();
  const lowerNeedle = needle.toLowerCase();
  const parts = [];
  let cursor = 0;
  let idx = lowerHaystack.indexOf(lowerNeedle, cursor);
  let key = 0;
  while (idx !== -1) {
    if (idx > cursor) parts.push(haystack.slice(cursor, idx));
    parts.push(
      <mark key={`m${key++}`} className="results-spreadsheet-mark">
        {haystack.slice(idx, idx + needle.length)}
      </mark>
    );
    cursor = idx + needle.length;
    idx = lowerHaystack.indexOf(lowerNeedle, cursor);
  }
  if (cursor < haystack.length) parts.push(haystack.slice(cursor));
  return parts.length > 0 ? parts : text;
}

function ResultsSpreadsheetPage({ user, isPro, onUpgrade }) {
  const [sheets, setSheets] = useState([]);
  const [activeSheet, setActiveSheet] = useState(() => {
    try {
      return localStorage.getItem('resultsSpreadsheet.activeSheet') || '';
    } catch (_err) {
      return '';
    }
  });
  const [rows, setRows] = useState([]);
  const [loadingSheets, setLoadingSheets] = useState(false);
  const [loadingRows, setLoadingRows] = useState(false);
  const [error, setError] = useState(null);
  const [requiresMembership, setRequiresMembership] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);
  const pickerRef = useRef(null);

  const signedIn = Boolean(user?.email);

  // Close the sheet picker on outside click or Escape so it behaves like a
  // standard popover (Google Sheets' bottom-left "all sheets" menu).
  useEffect(() => {
    if (!pickerOpen) return undefined;
    const handlePointer = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) {
        setPickerOpen(false);
      }
    };
    const handleKey = (e) => {
      if (e.key === 'Escape') setPickerOpen(false);
    };
    document.addEventListener('mousedown', handlePointer);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handlePointer);
      document.removeEventListener('keydown', handleKey);
    };
  }, [pickerOpen]);

  const fetchSheets = useCallback(async () => {
    if (!signedIn) return;
    setLoadingSheets(true);
    setError(null);
    try {
      const resp = await fetch(API_ENDPOINTS.resultsSpreadsheetSheets, {
        headers: buildAuthHeaders(),
      });
      const data = await resp.json().catch(() => ({}));
      if (resp.status === 403 && data?.requiresMembership) {
        setRequiresMembership(true);
        setSheets([]);
        return;
      }
      if (!resp.ok || !data?.success) {
        throw new Error(data?.error || `Failed to load sheets (HTTP ${resp.status})`);
      }
      setRequiresMembership(false);
      setSheets(Array.isArray(data.sheets) ? data.sheets : []);
    } catch (err) {
      setError(err.message || 'Failed to load sheets');
      setSheets([]);
    } finally {
      setLoadingSheets(false);
    }
  }, [signedIn]);

  const fetchRows = useCallback(async (sheetName) => {
    if (!signedIn || !sheetName) return;
    setLoadingRows(true);
    setError(null);
    try {
      const resp = await fetch(API_ENDPOINTS.resultsSpreadsheetData(sheetName), {
        headers: buildAuthHeaders(),
      });
      const data = await resp.json().catch(() => ({}));
      if (resp.status === 403 && data?.requiresMembership) {
        setRequiresMembership(true);
        setRows([]);
        return;
      }
      if (!resp.ok || !data?.success) {
        throw new Error(data?.error || `Failed to load "${sheetName}" (HTTP ${resp.status})`);
      }
      setRows(Array.isArray(data.rows) ? data.rows : []);
    } catch (err) {
      setError(err.message || 'Failed to load sheet');
      setRows([]);
    } finally {
      setLoadingRows(false);
    }
  }, [signedIn]);

  useEffect(() => {
    fetchSheets();
  }, [fetchSheets]);

  useEffect(() => {
    if (!sheets.length) return;
    const exists = sheets.some((s) => s.title === activeSheet);
    if (!exists) {
      setActiveSheet(sheets[0].title);
    }
  }, [sheets, activeSheet]);

  useEffect(() => {
    if (!activeSheet) return;
    try {
      localStorage.setItem('resultsSpreadsheet.activeSheet', activeSheet);
    } catch (_err) {
      /* ignore */
    }
    setSearchQuery('');
    fetchRows(activeSheet);
  }, [activeSheet, fetchRows]);

  // ---- Derived rendering data ---------------------------------------------
  const caption = useMemo(() => detectCaption(rows), [rows]);
  const headerRowIdx = caption ? 1 : 0;
  const headerRow = useMemo(
    () => (rows[headerRowIdx] ? rows[headerRowIdx].map(normalizeCell) : []),
    [rows, headerRowIdx]
  );
  const bodyRowsRaw = useMemo(
    () => (rows.length > headerRowIdx + 1 ? rows.slice(headerRowIdx + 1) : []),
    [rows, headerRowIdx]
  );
  const classifiedBodyRows = useMemo(
    () => bodyRowsRaw.map(classifyRow),
    [bodyRowsRaw]
  );
  const columnCount = useMemo(() => {
    let max = headerRow.length;
    for (const r of classifiedBodyRows) {
      if (r.cells.length > max) max = r.cells.length;
    }
    return max;
  }, [headerRow, classifiedBodyRows]);

  const lowerQuery = searchQuery.trim().toLowerCase();
  const filteredRows = useMemo(() => {
    if (!lowerQuery) return classifiedBodyRows;
    return classifiedBodyRows.filter((r) => {
      if (r.kind === 'blank') return false;
      return rowMatchesQuery(r.cells, lowerQuery);
    });
  }, [classifiedBodyRows, lowerQuery]);

  // ---- Gates ---------------------------------------------------------------
  // The brand name comes from the active entity, never a literal: this page is
  // per brand, and telling a Kairos member they need a "Vector Whop membership"
  // names a company they have no reason to have heard of.
  const brand = getEntityDisplayName();

  if (!signedIn) {
    return (
      <div className="results-spreadsheet-page">
        <div className="results-spreadsheet-gate">
          <IconLock size={36} />
          <h2>Sign in to view the {brand} Results Spreadsheet</h2>
          <p>You need to be signed in with your {brand} Whop account to browse the live results.</p>
        </div>
      </div>
    );
  }

  if (requiresMembership) {
    return (
      <div className="results-spreadsheet-page">
        <div className="results-spreadsheet-gate">
          <IconLock size={36} />
          <h2>{brand} Whop access required</h2>
          <p>Your account isn&apos;t linked to an active {brand} Whop membership yet.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="results-spreadsheet-page">
      <header className="results-spreadsheet-header">
        <PageHeading title="Results Spreadsheet" description="Raw strategy results in spreadsheet form." />
        {!isPro && onUpgrade && (
          <button
            type="button"
            className="results-spreadsheet-upsell"
            onClick={() => onUpgrade('Upgrade to RiskLo Pro for risk analytics, Portfolio Simulator, and more.')}
          >
            <IconStar size={16} />
            <span>Unlock analytics with Pro</span>
          </button>
        )}
      </header>

      {loadingSheets && sheets.length === 0 ? (
        <div className="results-spreadsheet-loading">
          <IconRefresh size={20} />
          <span>Loading sheets…</span>
        </div>
      ) : null}

      {sheets.length > 0 && (
        <div className="results-spreadsheet-tabbar">
          <div className="results-spreadsheet-picker" ref={pickerRef}>
            <button
              type="button"
              className={`results-spreadsheet-picker-btn ${pickerOpen ? 'open' : ''}`}
              onClick={() => setPickerOpen((v) => !v)}
              aria-haspopup="listbox"
              aria-expanded={pickerOpen}
              aria-label="All sheets"
              title="All sheets"
            >
              <IconList size={16} />
            </button>
            {pickerOpen && (
              <ul
                className="results-spreadsheet-picker-menu"
                role="listbox"
                aria-label="Sheets"
              >
                {sheets.map((s) => {
                  const isActive = s.title === activeSheet;
                  return (
                    <li key={s.title}>
                      <button
                        type="button"
                        role="option"
                        aria-selected={isActive}
                        className={`results-spreadsheet-picker-item ${isActive ? 'active' : ''}`}
                        onClick={() => {
                          setActiveSheet(s.title);
                          setPickerOpen(false);
                        }}
                      >
                        <span className="results-spreadsheet-picker-check" aria-hidden="true">
                          {isActive ? <IconCheck size={14} /> : null}
                        </span>
                        <span className="results-spreadsheet-picker-label">{s.title}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
          <nav className="results-spreadsheet-tabs" aria-label="Spreadsheet tabs">
            {sheets.map((s) => (
              <button
                key={s.title}
                type="button"
                className={`results-spreadsheet-tab ${activeSheet === s.title ? 'active' : ''}`}
                onClick={() => setActiveSheet(s.title)}
                title={s.title}
              >
                {s.title}
              </button>
            ))}
          </nav>
        </div>
      )}

      {sheets.length > 0 && activeSheet && (
        <div className="results-spreadsheet-toolbar">
          <div className="results-spreadsheet-search">
            <IconSearch size={16} />
            <input
              type="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={`Search ${activeSheet}…`}
              aria-label={`Search ${activeSheet}`}
            />
            {searchQuery && (
              <button
                type="button"
                className="results-spreadsheet-search-clear"
                onClick={() => setSearchQuery('')}
                aria-label="Clear search"
              >
                <IconX size={14} />
              </button>
            )}
          </div>
          {lowerQuery && (
            <span className="results-spreadsheet-search-count">
              {filteredRows.length} match{filteredRows.length === 1 ? '' : 'es'}
            </span>
          )}
        </div>
      )}

      {error && (
        <div className="results-spreadsheet-error" role="alert">
          {error}
        </div>
      )}

      <div className="results-spreadsheet-table-wrap">
        {loadingRows ? (
          <div className="results-spreadsheet-loading">
            <IconRefresh size={20} />
            <span>Loading {activeSheet}…</span>
          </div>
        ) : rows.length === 0 ? (
          <div className="results-spreadsheet-empty">
            {activeSheet
              ? `No data in "${activeSheet}".`
              : 'Select a sheet to view its data.'}
          </div>
        ) : lowerQuery && filteredRows.length === 0 ? (
          <div className="results-spreadsheet-empty">
            No rows match “{searchQuery}”.
          </div>
        ) : (
          <table className="results-spreadsheet-table">
            {caption && (
              <caption className="results-spreadsheet-caption">{caption}</caption>
            )}
            <thead>
              <tr>
                {Array.from({ length: columnCount }).map((_, idx) => (
                  <th key={idx} scope="col">
                    {highlightMatch(headerRow[idx] ?? '', lowerQuery ? searchQuery : '')}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((row, rIdx) => {
                if (row.kind === 'blank') {
                  return (
                    <tr key={rIdx} className="results-spreadsheet-row-blank" aria-hidden="true">
                      <td colSpan={columnCount}>&nbsp;</td>
                    </tr>
                  );
                }
                const rowClass = `results-spreadsheet-row results-spreadsheet-row-${row.kind}`;
                return (
                  <tr key={rIdx} className={rowClass}>
                    {Array.from({ length: columnCount }).map((_, cIdx) => {
                      const value = row.cells[cIdx] ?? '';
                      const negative = row.kind === 'data' && isNegativeCell(value);
                      const cellClass = negative ? 'is-negative' : '';
                      return (
                        <td key={cIdx} className={cellClass}>
                          {highlightMatch(value, lowerQuery ? searchQuery : '')}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default ResultsSpreadsheetPage;
